import { isAxiosError } from 'axios';
import { API_ROUTES } from '@/api/apiRoutes';
import { log } from '@/logger/logger';
import { safeAxios } from '@/services/safeAxios';
import { pollPredictionStatus } from './modelPrediction';
import { ModelRunError } from './errors';
import type { ModelRunCredits } from './modelPrediction';
import type { ModelType } from '@/enums/model-type.enum';

const logger = log.getLogger('runModel.api');
// Shared across runs because their callbacks update the same credit balances.
let creditsRevision = 0;

interface ModelRequest {
  model: { name: string; type: ModelType; version: string; isImported?: boolean };
  input: Record<string, unknown>;
  recipeId: string;
  recipeVersion: number;
}

export interface ModelRunObservation {
  signal?: AbortSignal;
  keepTracking?: boolean;
  onProgress?: (progress: number) => void;
  /** Exposes cancellation independently of the lifetime of the mounted panel. */
  onCancelReady?: (cancel: () => Promise<void>) => void;
  /** Discards the caller's pending results when cancellation is accepted. */
  onCanceled?: (predictionId: string) => void;
}

/**
 * Uses the node runner's authenticated endpoint and polling, without a node destination.
 * The server still records/bills the prediction, but addResultsToNode skips a missing nodeId.
 * A timeout stops waiting, not generation; never automatically retry a chargeable POST.
 */
export async function runModelWithoutNode(
  request: ModelRequest,
  onCredits?: (credits: ModelRunCredits) => void,
  observation: ModelRunObservation = {},
): Promise<unknown[]> {
  const updateCredits = (credits: ModelRunCredits) => {
    if (!onCredits) return;
    if (credits.remainingCredits !== undefined || credits.userRemainingCredits !== undefined) creditsRevision++;
    onCredits(credits);
  };
  const controller = new AbortController();
  let predictionId: string | undefined;
  let canceled = false;
  let succeeded = false;
  let creationFinished!: (id: string | undefined) => void;
  const created = new Promise<string | undefined>((resolve) => {
    creationFinished = resolve;
  });
  let cancellation: Promise<void> | undefined;
  const cancelPrediction = () => {
    if (cancellation) return cancellation;
    cancellation = (async () => {
      // Keep creation alive so a cancellation during upload can use the returned ID.
      const id = await created;
      if (!id || observation.signal?.aborted) return;
      if (succeeded) {
        // Generation is complete, but the caller may still be preparing its results.
        observation.onCanceled?.(id);
        return;
      }
      if (controller.signal.aborted) return;
      try {
        await safeAxios.post(API_ROUTES.models.cancelPrediction(), { predictionId: id });
        if (observation.signal?.aborted) return;
        canceled = true;
        observation.onCanceled?.(id);
        controller.abort();
        try {
          while (!observation.signal?.aborted) {
            const refundRevision = creditsRevision;
            const response = await safeAxios.get<ModelRunCredits>(
              API_ROUTES.models.predictionStatus({ predictionId: id }),
              { signal: observation.signal },
            );
            if (observation.signal?.aborted) return;
            // Another response can contain an older balance. Fetch again after that
            // response instead of assuming it includes this refund.
            if (refundRevision !== creditsRevision) continue;
            updateCredits({
              remainingCredits: response.data.remainingCredits,
              userRemainingCredits: response.data.userRemainingCredits,
            });
            break;
          }
        } catch (error) {
          if (!observation.signal?.aborted) logger.warn('Failed to refresh credits after canceling prediction', error);
        }
      } catch (error) {
        logger.error('Failed to cancel model prediction', error);
        throw error;
      }
    })().finally(() => {
      cancellation = undefined;
    });
    return cancellation;
  };
  observation.onCancelReady?.(cancelPrediction);
  const timer = setTimeout(() => controller.abort(), 10 * 60_000);
  const stopMonitoring = () => controller.abort();
  observation.signal?.addEventListener('abort', stopMonitoring, { once: true });
  try {
    if (observation.signal?.aborted) controller.abort();
    controller.signal.throwIfAborted();
    const creationCreditsRevision = creditsRevision;
    const response = await safeAxios.post<ModelRunCredits & { predictionId?: string }>(
      API_ROUTES.models.run(),
      request,
      {
        'axios-retry': { retries: 0 },
        signal: controller.signal,
      },
    );
    predictionId = response.data.predictionId;
    creationFinished(predictionId);
    if (controller.signal.aborted) controller.signal.throwIfAborted();
    // A canceled run can finish creation after its replacement charged. Preserve
    // that balance until cancellation refreshes it after the refund.
    if (!cancellation || creationCreditsRevision === creditsRevision) {
      updateCredits({
        remainingCredits: response.data.remainingCredits,
        userRemainingCredits: response.data.userRemainingCredits,
      });
    }
    if (!predictionId) throw new ModelRunError('missing_prediction');
    if (observation.keepTracking) clearTimeout(timer);
    const id = predictionId;

    return await new Promise<unknown[]>((resolve, reject) => {
      const onAbort = () => reject(new ModelRunError(canceled ? 'canceled' : 'timeout', predictionId));
      if (controller.signal.aborted) {
        onAbort();
        return;
      }
      controller.signal.addEventListener('abort', onAbort, { once: true });
      let pollCreditsRevision = creditsRevision;
      void pollPredictionStatus(
        id,
        {
          onRequestStart: () => {
            pollCreditsRevision = creditsRevision;
          },
          onProgress: observation.onProgress,
          onSuccess: (results) => {
            succeeded = true;
            observation.onProgress?.(100);
            resolve(results);
          },
          onError: (error: unknown) =>
            reject(new ModelRunError('failed', predictionId, typeof error === 'string' ? error : undefined)),
          onStatusChange: (status, remainingCredits, userRemainingCredits) => {
            if (controller.signal.aborted) return;
            if (pollCreditsRevision === creditsRevision) {
              updateCredits({ remainingCredits, userRemainingCredits });
            }
            if (status === 'canceled') reject(new ModelRunError('canceled', predictionId));
          },
        },
        () => controller.signal.aborted,
        controller.signal,
        { retryTransientErrors: true },
      );
    });
  } catch (error) {
    if (error instanceof ModelRunError) throw error;
    const serverMessage = isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined;
    const message =
      typeof serverMessage === 'string' ? serverMessage : error instanceof Error ? error.message : undefined;
    throw new ModelRunError(
      canceled ? 'canceled' : controller.signal.aborted ? 'timeout' : 'failed',
      predictionId,
      message,
      { cause: error },
    );
  } finally {
    clearTimeout(timer);
    observation.signal?.removeEventListener('abort', stopMonitoring);
    creationFinished(undefined);
    // Also stops a scheduled poll if a terminal callback has already settled the promise.
    controller.abort();
  }
}
